import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import clsx from 'clsx';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label: string;
  options: SelectOption[];
  error?: string;
  hint?: string;
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, error, hint, placeholder, className, id, ...rest },
  ref,
) {
  const reactId = useId();
  const selectId = id ?? `sel-${reactId}`;
  const hintId = hint ? `${selectId}-hint` : undefined;
  const errorId = error ? `${selectId}-error` : undefined;

  return (
    <div className={clsx('relative', className)}>
      <label
        htmlFor={selectId}
        className="block font-mono text-[0.625rem] uppercase tracking-[0.125em] text-ink-subtle mb-1.5"
      >
        {label}
      </label>
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={clsx(hintId, errorId) || undefined}
          className={clsx(
            'w-full appearance-none bg-panel border rounded-md pl-3 pr-10 py-2 min-h-[2.75rem] text-[0.9375rem] font-display',
            'transition-colors duration-200',
            'focus:outline-none focus:border-blue focus:ring-2 focus:ring-blue/30',
            error
              ? 'border-[#b33a3a]'
              : 'border-hairline hover:border-ink/30',
          )}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-[0.75rem] text-ink-subtle">
          {hint}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.1em] text-[#b33a3a]"
          role="alert"
        >
          &gt; {error}
        </p>
      )}
    </div>
  );
});
